import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';
import { useSignupConversionTracking } from '../hooks/useSignupConversionTracking';
import { useApplyFormConfig } from '../hooks/useFlags';
import { countryFromPath } from '../constants/routes';
import {
  cityPickerSource,
  envApplyFormConfig,
  parsePilotConfig,
  pilotCitiesFor,
  type ApplyFormSource,
} from '../helpers/applyFormSource';
import { ANONYMOUS_ID } from '../helpers/tracking';
import { getCookieConsentValue } from '../helpers/cookies';

const DECISION_TIMEOUT_MS = 1500;
// The attribution keys the Waterfall form keeps (lib/embed.ts in waterfall-frontend); nothing else is forwarded.
const WATERFALL_ATTRIBUTION_KEYS = new Set([
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'fbclid',
  'ttclid',
  'msclkid',
  'li_fat_id',
  'twclid',
  'sccid',
  'rdt_cid',
  'jcc',
  'ref',
  'referrer',
  'referral_code',
  'referralcode',
]);

const isWaterfallAttributionKey = (key: string) => {
  const k = key.toLowerCase();
  return k.startsWith('utm_') || WATERFALL_ATTRIBUTION_KEYS.has(k);
};

function readAnonymousId(): string | null {
  try {
    const stored = localStorage.getItem(ANONYMOUS_ID);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return typeof parsed === 'string' ? parsed : stored;
  } catch {
    return null;
  }
}

function originOf(url: string): string | undefined {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

// The form URL with the page's query string, exactly as main built it: utm_content is the anonymous
// id, REFERRALCODE becomes utm_term, and the Waterfall form learns about accepted marketing cookies.
// formParams tag a page's own applicants and replace the visitor's values. A REFERRALCODE is a
// referral: its value becomes the only utm_term, over any utm_term the visitor or the page brings.
export function formURL(
  source: ApplyFormSource,
  pageURL: string,
  anonymousId: string | null,
  formParams: Record<string, string> = {},
): string {
  const searchParams = new URL(pageURL).searchParams;
  Object.entries(formParams).forEach(([key, value]) => searchParams.set(key, value));
  const referralCode = searchParams.get('REFERRALCODE');
  if (referralCode !== null) {
    searchParams.delete('REFERRALCODE');
    searchParams.set('utm_term', referralCode);
  }
  if (anonymousId) searchParams.set('utm_content', anonymousId);
  if (source.provider === 'waterfall') {
    Array.from(new Set(searchParams.keys()))
      .filter((key) => !isWaterfallAttributionKey(key))
      .forEach((key) => searchParams.delete(key));
    if (getCookieConsentValue() === 'true') searchParams.set('consent', '1');
  }
  const queryString = searchParams.toString();
  if (!queryString) return source.url;
  const separator = /[?&]$/.test(source.url) ? '' : source.url.includes('?') ? '&' : '?';
  return `${source.url}${separator}${queryString}`;
}

type SignUpSectionProps = {
  formParams?: Record<string, string>;
  // A Fountain opening of its own, in place of the language's LP Redirect.
  fountainURL?: string;
  // Lets the frame scroll when the form outgrows it, for a page that sizes the frame to the form.
  frameScrolls?: boolean;
};

const SignUpSection = ({ formParams, fountainURL, frameScrolls = false }: SignUpSectionProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const country = countryFromPath(pathname);
  const lang = pathname.split('/')[1] ?? '';
  const envConfig = useMemo(() => envApplyFormConfig(), []);
  // Without a Waterfall base URL no city can be offered, so the page never waits for Eppo.
  const canOfferWaterfall = Boolean(process.env.REACT_APP_WATERFALL_BASE_URL);
  const { settled, pending, value } = useApplyFormConfig(envConfig === null && canOfferWaterfall);
  const config = useMemo(() => envConfig ?? parsePilotConfig(value), [envConfig, value]);

  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setTimedOut(true), DECISION_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);
  const decided = !pending || timedOut;

  // The Fountain LP Redirect of this language, as on every page before the pilot.
  const fountain = useMemo<ApplyFormSource>(
    () => ({ provider: 'fountain', url: fountainURL ?? t('signup_section.form.address') }),
    [t, fountainURL],
  );
  const [source, setSource] = useState<ApplyFormSource | null>(null);
  const { enableTracking, trackFormShown } = useSignupConversionTracking(
    source ? originOf(source.url) : undefined,
  );

  // The first decision sticks: a late Eppo answer never swaps a form the visitor already sees.
  const decidedOnce = useRef(false);
  useEffect(() => {
    if (!decided || decidedOnce.current) return;
    decidedOnce.current = true;
    const pilots = pilotCitiesFor(config, country);
    const chosen = cityPickerSource(pilots, lang) ?? fountain;
    setSource(chosen);
    trackFormShown(chosen.provider, settled || envConfig !== null || !canOfferWaterfall);
  }, [
    decided,
    config,
    country,
    lang,
    fountain,
    settled,
    envConfig,
    canOfferWaterfall,
    trackFormShown,
  ]);

  const signUpURL = useMemo(
    () => (source ? formURL(source, window.location.href, readAnonymousId(), formParams) : ''),
    [source, formParams],
  );

  const frameRef = useRef<HTMLIFrameElement>(null);
  const [frameHeight, setFrameHeight] = useState<number>();
  const waterfallOrigin = source?.provider === 'waterfall' ? originOf(signUpURL) : undefined;
  useEffect(() => {
    if (!waterfallOrigin) return;
    const handler = (event: MessageEvent<{ eventName?: string; height?: unknown } | null>) => {
      if (event.origin !== waterfallOrigin) return;
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      if (event.data?.eventName === 'leave') {
        setFrameHeight(undefined);
        return;
      }
      if (event.data?.eventName !== 'resize') return;
      const next = event.data.height;
      if (typeof next === 'number' && Number.isFinite(next) && next > 0)
        setFrameHeight(Math.ceil(next));
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [waterfallOrigin]);

  const [isSignUpModalVisible, setIsSignUpModalVisible] = useState(false);

  const renderRiderModal = () => {
    return (
      <Modal isOpen={isSignUpModalVisible} onCancel={() => setIsSignUpModalVisible(false)}>
        <div className="flex h-600 flex-col items-start overflow-auto bg-white px-6 pb-12 pt-16 text-left text-blue-prussian">
          {/* <h2 className="text-lg">{t('signup_section.modal.title')}</h2> */}
          <div className="mt-2">
            <div
              className="pt-4 font-flink-regular text-sm"
              dangerouslySetInnerHTML={{ __html: t('signup_section.modal.description') }}
            />
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <section
      id="apply_now"
      className="bg-flinkRose-light py-16 px-5 text-center font-flink-light text-pink-deep"
    >
      <div className="signup-section-animated mx-auto max-w-6xl shadow-lg shadow-rose-500/50 drop-shadow-lg">
        <div className="mx-auto bg-white">
          <div className="w-full">
            <div className="mx-auto max-w-2xl pt-10 lg:text-5xl">
              <h3 className={`max-w-2xl px-4 text-4xl md:text-5xl`}>{t('signup_section.title')}</h3>
            </div>
            {source !== null && (
              <>
                <div className="">
                  {/* Stopgap fixed-height workaround: keep this large enough to avoid clipping the full form. */}
                  <iframe
                    ref={frameRef}
                    data-cy="signup-iframe"
                    data-cy-provider={source.provider}
                    src={signUpURL}
                    allow={waterfallOrigin ? `geolocation ${waterfallOrigin}` : undefined}
                    width="100"
                    height="100"
                    scrolling={frameScrolls ? 'auto' : 'no'}
                    className="h-[2500px] w-full truncate"
                    style={frameHeight ? { height: frameHeight } : undefined}
                    onLoad={enableTracking}
                  ></iframe>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {renderRiderModal()}
    </section>
  );
};

export default SignUpSection;
